import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { storage } from "../config/firebase";

const RESUME_CURRENT_FOLDER = "resumes/current";
const CERTIFICATE_LOGO_FOLDER = "certificates/logos";
const MAX_LOGO_BYTES = 2 * 1024 * 1024;

const CACHE_IMMUTABLE = "public, max-age=31536000, immutable";

function timestampToken() {
  return new Date().toISOString().replace(/[:.]/g, "-");
}

function buildResumeFileName() {
  return `resume-${timestampToken()}.pdf`;
}

function buildCurrentPath(fileName) {
  return `${RESUME_CURRENT_FOLDER}/${fileName}`;
}

export async function getResumeDownloadUrl(resumePath) {
  if (!resumePath) return "";
  return getDownloadURL(ref(storage, resumePath));
}

export async function replaceResumeFile(file, previousResumePath = "") {
  const fileName = buildResumeFileName();
  const currentPath = buildCurrentPath(fileName);
  const currentRef = ref(storage, currentPath);

  await uploadBytes(currentRef, file, {
    contentType: file.type || "application/pdf",
    cacheControl: CACHE_IMMUTABLE,
  });

  const resumeUrl = await getDownloadURL(currentRef);

  return {
    resume_url: resumeUrl,
    resume_storage_url: resumeUrl,
    resume_path: currentPath,
    resume_name: fileName,
    resume_updated_at: new Date().toISOString(),
    archived_resume_path: previousResumePath || "",
  };
}

function logoExtension(file) {
  const map = {
    "image/png": ".png",
    "image/jpeg": ".jpg",
    "image/webp": ".webp",
    "image/svg+xml": ".svg",
    "image/gif": ".gif",
  };
  return map[file.type] || "";
}

export function validateCertificateLogo(file) {
  if (!file.type.startsWith("image/")) {
    throw new Error("Logo must be an image file (PNG, JPG, WebP, SVG).");
  }
  if (file.size > MAX_LOGO_BYTES) {
    throw new Error("Logo must be smaller than 2 MB.");
  }
}

export async function uploadCertificateLogo(file) {
  validateCertificateLogo(file);

  const path = `${CERTIFICATE_LOGO_FOLDER}/logo-${timestampToken()}${logoExtension(file)}`;
  const logoRef = ref(storage, path);

  await uploadBytes(logoRef, file, {
    contentType: file.type,
    cacheControl: CACHE_IMMUTABLE,
  });
  const url = await getDownloadURL(logoRef);

  return { url, path };
}

export async function deleteCertificateLogo(path) {
  if (!path) return;
  await deleteObject(ref(storage, path));
}

// ── Blog images ───────────────────────────────────────────

const BLOG_IMAGE_FOLDER = "blog/images";
const MAX_BLOG_IMAGE_BYTES = 5 * 1024 * 1024;

export function validateBlogImage(file) {
  if (!file.type.startsWith("image/")) {
    throw new Error("File must be an image (PNG, JPG, WebP, GIF).");
  }
  if (file.size > MAX_BLOG_IMAGE_BYTES) {
    throw new Error("Image must be smaller than 5 MB.");
  }
}

export async function uploadBlogImage(file) {
  validateBlogImage(file);

  const path = `${BLOG_IMAGE_FOLDER}/img-${timestampToken()}${logoExtension(file)}`;
  const imgRef = ref(storage, path);

  await uploadBytes(imgRef, file, {
    contentType: file.type,
    cacheControl: CACHE_IMMUTABLE,
  });
  const url = await getDownloadURL(imgRef);

  return { url, path };
}

export async function deleteBlogImage(path) {
  if (!path) return;
  await deleteObject(ref(storage, path));
}